import { apiSlice } from './apiSlice'

/** Mirrors FastAPI `SaleDebitNoteOut` (camelCase) — frmCtrlSalesQryDebit datasheet. */
export interface SaleDebitNote {
  id: number
  debitNo: string | null
  claimScsCode: string | null
  debitDate: string | null
  debitorToName: string | null
  debitCpCode: string | null
  debitorRefNo: string | null
  debitCpName: string | null
  debitReason: string | null
  debitValue: number | null
  note: string | null
  dateUpdate: string | null
  personUpdate: string | null
  claimType: string | null
  priceUnit: string | null
  fx: number | null
  unit: string | null
  currency: string | null
}

export interface SaleDebitNoteListResponse {
  items: SaleDebitNote[]
  total: number
  page: number
  pageSize: number
}

export interface SaleDebitNoteListQuery {
  page: number
  pageSize: number
  deliveryPeriodFrom?: string
  deliveryPeriodTo?: string
  finalizedFilter?: 'all' | 'yes' | 'no'
}

export interface SuggestIndexResponse {
  index: number
  exists: boolean
}

export interface DnLookupItem { value: string; label: string; meta?: { debitorTo?: string; name?: string } | null }
export interface DnLookupResponse { items: DnLookupItem[]; total: number }

/** GET /{id}/form shape for Edit hydrate. */
export type SaleDebitNoteForm = Record<string, unknown> & { id: number }

/** claim_scs_code_AfterUpdate DLookUps over v_claim_scs (readonly display). */
export interface DebitClaimSnapshot {
  claimType: string | null
  claimValue: number | null
  claimCurrency: string | null
  invoiceNo: string | null
}

/** Create/Update payload — frmCtrlSalesQryDebitAdd → claim_debit_scs. */
export interface SaleDebitNoteWrite {
  claimScsCode?: string | null
  debitNo?: string | null
  debitDate?: string | null
  debitorTo?: string | null
  debitCpCode?: string | null
  debitorRefNo?: string | null
  debitReason?: string | null
  debitValue?: number | null
  note?: string | null
  priceUnit?: string | null
  fx?: number | null
}

const saleDebitNoteApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSaleDebitNotes: builder.query<SaleDebitNoteListResponse, SaleDebitNoteListQuery>({
      query: ({ page, pageSize, deliveryPeriodFrom, deliveryPeriodTo, finalizedFilter }) => ({
        url: '/sale-debit-notes',
        params: {
          page,
          pageSize,
          ...(deliveryPeriodFrom ? { deliveryPeriodFrom } : {}),
          ...(deliveryPeriodTo ? { deliveryPeriodTo } : {}),
          ...(finalizedFilter ? { finalizedFilter } : {}),
        },
      }),
      providesTags: ['SaleDebitNote'],
    }),

    getSaleDebitNote: builder.query<SaleDebitNote, number>({
      query: (id) => `/sale-debit-notes/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'SaleDebitNote', id }],
    }),

    deleteSaleDebitNote: builder.mutation<{ message: string }, number>({
      query: (id) => ({ url: `/sale-debit-notes/${id}`, method: 'DELETE' }),
      invalidatesTags: ['SaleDebitNote'],
    }),

    getSaleDebitNoteCanHardDelete: builder.query<{ canDelete: boolean }, void>({
      query: () => '/sale-debit-notes/access/can-delete',
    }),

    // ── frmCtrlSalesQryDebitAdd ──────────────────────────────────────────────
    getSaleDebitNoteForm: builder.query<SaleDebitNoteForm, number>({
      query: (id) => `/sale-debit-notes/${id}/form`,
    }),
    createSaleDebitNote: builder.mutation<{ claim_debit_scs_id: number }, SaleDebitNoteWrite>({
      query: (body) => ({ url: '/sale-debit-notes', method: 'POST', body }),
      invalidatesTags: ['SaleDebitNote'],
    }),
    updateSaleDebitNote: builder.mutation<{ claim_debit_scs_id: number }, { id: number; body: SaleDebitNoteWrite }>({
      query: ({ id, body }) => ({ url: `/sale-debit-notes/${id}`, method: 'PUT', body }),
      invalidatesTags: ['SaleDebitNote'],
    }),
    suggestDebitNo: builder.query<SuggestIndexResponse, void>({
      query: () => '/sale-debit-notes/suggest/debit-no',
    }),
    checkDebitNo: builder.query<SuggestIndexResponse, { debitNo: string; excludeId?: number }>({
      query: ({ debitNo, excludeId }) => ({
        url: '/sale-debit-notes/check/debit-no',
        params: { debitNo, ...(excludeId ? { excludeId } : {}) },
      }),
      keepUnusedDataFor: 0,
    }),
    getDebitClaimSnapshot: builder.query<DebitClaimSnapshot, string>({
      query: (claimScsCode) => ({ url: '/sale-debit-notes/claim-snapshot', params: { claimScsCode } }),
      keepUnusedDataFor: 0,
    }),
    dnLookupClaimCodes: builder.query<DnLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-debit-notes/lookups/claim-codes', params: q ? { q } : {} }),
    }),
    dnLookupPriceUnits: builder.query<DnLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-debit-notes/lookups/price-units', params: q ? { q } : {} }),
    }),
    dnLookupCpList: builder.query<DnLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-debit-notes/lookups/cp-list', params: q ? { q } : {} }),
    }),
    dnLookupServices: builder.query<DnLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-debit-notes/lookups/services', params: q ? { q } : {} }),
    }),
  }),
})

export const {
  useGetSaleDebitNotesQuery,
  useGetSaleDebitNoteQuery,
  useDeleteSaleDebitNoteMutation,
  useGetSaleDebitNoteCanHardDeleteQuery,
  useLazyGetSaleDebitNoteFormQuery,
  useCreateSaleDebitNoteMutation,
  useUpdateSaleDebitNoteMutation,
  useLazySuggestDebitNoQuery,
  useLazyCheckDebitNoQuery,
  useLazyGetDebitClaimSnapshotQuery,
  useDnLookupClaimCodesQuery,
  useDnLookupPriceUnitsQuery,
  useDnLookupCpListQuery,
  useDnLookupServicesQuery,
} = saleDebitNoteApi

export default saleDebitNoteApi
